/**
 * Configuration du Mode Simple/Expert
 *
 * Ce fichier définit quelles sections et routes sont visibles
 * selon le mode UI sélectionné par l'utilisateur.
 */

/**
 * IDs des sections du sidebar
 * Correspondent aux sections dans DashboardMainResponsive.tsx
 */
export type SectionId =
  | 'dashboard'
  | 'sales'        // Ventes: Devis, Factures, Avoirs, Relances
  | 'purchases'    // Achats: Dépenses, OCR
  | 'catalog'      // Catalogue: Clients, Produits, Fournisseurs
  | 'treasury'     // Trésorerie
  | 'tools'        // Outils: Imports, IA, Benchmarks, Financement
  | 'benchmarks'   // Intelligence Collective (sous-item de tools)
  | 'financing'    // Financement (sous-item de tools)
  | 'ai'           // IA CFO (sous-item de tools)
  | 'tax'          // Déclarations TVA
  | 'accounting'   // Comptabilité (inclut conformité)
  | 'compliance'   // Conformité (intégré dans comptabilité)
  | 'crm'          // CRM Vente
  | 'erp'          // ERP Chantier
  | 'interventions'// Interventions
  | 'admin'        // Administration (unifié: équipe + docs + intégrations + monitoring)
  | 'settings'     // Paramètres
  | 'help'         // Aide
  | 'superadmin';  // SuperAdmin

interface SimpleModeConfig {
  /** Sections visibles en mode Simple */
  visibleSections: SectionId[];

  /** Routes accessibles en mode Simple */
  visibleRoutes: string[];

  /** IDs des menus à cacher */
  hiddenMenuIds: SectionId[];

  /** Description du mode */
  description: string;

  /** Libellé court */
  label: string;
}

interface ExpertModeConfig {
  /** Toutes les sections visibles */
  visibleSections: 'all';

  /** Toutes les routes accessibles */
  visibleRoutes: 'all';

  /** Aucun menu caché (sauf role-based) */
  hiddenMenuIds: SectionId[];

  /** Description du mode */
  description: string;

  /** Libellé court */
  label: string;
}

export const UI_MODE_CONFIG = {
  simple: {
    label: 'Simple',
    description: 'Artisan solo - Essentiel uniquement',

    visibleSections: [
      'dashboard',
      'sales',          // Devis, Factures, Avoirs
      'catalog',        // Clients, Produits
      'crm',            // CRM Vente (natif BTP)
      'interventions',  // Interventions terrain (natif BTP)
      'settings',       // Paramètres
      'help',           // Aide
    ] as SectionId[],

    visibleRoutes: [
      '/dashboard',
      '/quotes',
      '/invoices',
      '/credit-notes',
      '/recurring-invoices',
      '/public-links',
      '/reminders',
      '/clients',
      '/products',
      '/categories',
      '/signatures',
      '/crm-vente',
      '/leads',
      '/opportunities',
      '/sales-tasks',
      '/automation-rules',
      '/interventions',
      '/work-orders',
      '/planning',
      '/field-mode',
      '/timesheets',
      '/settings',
      '/help',
      '/support',
    ],

    hiddenMenuIds: [
      'purchases',
      'treasury',
      'benchmarks',
      'financing',
      'ai',
      'tax',
      'accounting',
      'compliance',
      'erp',
      'admin',
      'superadmin',
    ] as SectionId[],
  } satisfies SimpleModeConfig,

  expert: {
    label: 'Expert',
    description: 'PME BTP - Toutes les fonctionnalités',

    visibleSections: 'all',
    visibleRoutes: 'all',

    // Seuls admin/superadmin sont cachés par rôle, pas par mode
    hiddenMenuIds: [] as SectionId[],
  } satisfies ExpertModeConfig,
} as const;

/**
 * Labels pour l'UI du toggle
 */
export const UI_MODE_LABELS = {
  simple: {
    title: 'Simple',
    subtitle: 'Artisan solo',
    description: 'Interface simplifiée avec uniquement les fonctionnalités essentielles : devis, factures, clients et produits.',
    icon: 'user',
  },
  expert: {
    title: 'Expert',
    subtitle: 'PME BTP',
    description: 'Accès complet : trésorerie, comptabilité, TVA, CRM, ERP chantier, IA CFO et plus encore.',
    icon: 'building2',
  },
} as const;

/**
 * Message affiché lors du passage en mode Expert
 */
export const EXPERT_MODE_WELCOME = {
  title: 'Mode Expert activé',
  message: 'Vous avez maintenant accès à toutes les fonctionnalités avancées. Découvrez la trésorerie, la comptabilité et le CRM dans le menu latéral.',
};

/**
 * Message affiché lors du passage en mode Simple
 */
export const SIMPLE_MODE_WELCOME = {
  title: 'Mode Simple activé',
  message: 'L\'interface a été simplifiée. Vous pouvez réactiver le mode Expert à tout moment dans Paramètres > Apparence.',
};
